import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useFloating, offset, flip, shift, arrow } from '@floating-ui/react-dom';
import styled from 'styled-components';
import { Transition } from 'react-transition-group';

import { Text } from '../../atoms';

// Styled tooltip container
const TooltipContainer = styled.div`
  display: flex;
  position: absolute;
  background: var(--gray-100);
  color: var(--white);
  padding: 4px 8px;
  border-radius: 8px;
  white-space: nowrap;
  z-index: 9999;
  opacity: 0;
  transform: translateY(5px);
  transition: opacity 0.2s ease, transform 0.2s ease;

  ${({ state }) =>
    state === 'entering' &&
    `
      opacity: 1;
      transform: translateY(0);
    `}

  ${({ state }) =>
    state === 'entered' &&
    `
      opacity: 1;
      transform: translateY(0);
    `}
  
  ${({ state }) =>
    state === 'exiting' &&
    `
      opacity: 0;
      transform: translateY(5px);
    `}
`;

// Styled arrow
const Arrow = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: var(--gray-100);
  transform: rotate(45deg);

  ${({ $placement, $arrowX }) =>
    $placement.includes('top') &&
    `
      bottom: -3px;
      left: ${$arrowX}px;
    `}

  ${({ $placement, $arrowX }) =>
    $placement.includes('bottom') &&
    `
      top: -3px;
      left: ${$arrowX}px;
    `}

  ${({ $placement, $arrowY }) =>
    $placement.includes('left') &&
    `
      right: -3px;
      top: ${$arrowY}px;
    `}

  ${({ $placement, $arrowY }) =>
    $placement.includes('right') &&
    `
      left: -3px;
      top: ${$arrowY}px;
    `}
`;

const Tooltip = forwardRef(
  (
    { className = null, children, content, placement = 'top', onClick = null, onMouseEnter = null },
    forwardedRef
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const arrowRef = useRef(null);

    const {
      x,
      y,
      refs,
      strategy,
      middlewareData,
      placement: calculatedPlacement
    } = useFloating({
      placement,
      middleware: [offset(8), flip(), shift(), arrow({ element: arrowRef, padding: 12 })]
    });

    const handleMouseEnter = useCallback(
      (evt) => {
        onMouseEnter?.(evt);
        if (content) {
          setIsOpen(true);
        }
      },
      [onMouseEnter, content]
    );

    const handleMouseLeave = useCallback(() => {
      if (content) {
        setIsOpen(false);
      }
    }, [content]);

    const setReference = useCallback(
      (node) => {
        refs.setReference(node);
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          // eslint-disable-next-line no-param-reassign
          forwardedRef.current = node;
        }
      },
      [refs.setReference, forwardedRef]
    );

    useImperativeHandle(forwardedRef, () => ({
      close: handleMouseLeave
    }));

    return (
      <div
        ref={setReference}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'inline-flex' }}
        className={className}
      >
        {React.cloneElement(children, {
          onClick: (evt) => {
            // Execute the child's onClick if it exists.
            if (children?.props?.onClick) {
              children.props.onClick(evt);
            }
            // Execute tooltip onClick logic if provided.
            if (onClick) {
              setIsOpen(false);
              onClick?.(evt);
            }
          }
        })}
        {createPortal(
          <Transition in={isOpen} timeout={200} unmountOnExit>
            {(state) => (
              <TooltipContainer
                ref={refs.setFloating}
                style={{ top: y ?? 0, left: x ?? 0, position: strategy }}
                state={state}
              >
                <Text as='div' $color='var(--white)'>
                  {content}
                </Text>
                <Arrow
                  ref={arrowRef}
                  $placement={calculatedPlacement}
                  $arrowX={middlewareData.arrow?.x ?? 0}
                  $arrowY={middlewareData.arrow?.y ?? 0}
                />
              </TooltipContainer>
            )}
          </Transition>,
          document.querySelector('#portal-root')
        )}
      </div>
    );
  }
);

Tooltip.propTypes = {
  placement: PropTypes.string,
  children: PropTypes.node.isRequired,
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  onMouseEnter: PropTypes.func,
  className: PropTypes.string,
  onClick: PropTypes.func
};

export { Tooltip };
